import { changeMobileForm } from 'utils/changeMobileForm';
import { CellProps } from 'models/Hospital/viewDetailPatientPageTypes';
import moment from 'moment';
import styled from 'styled-components';
import { font, palette } from 'styles/Global/globalStyles';
import { calculateAge, calculateBirthdayAge } from 'utils/calculateAge';
import { SquareLineSBadge } from 'components/Common/Badge';
import { Body2Medium, Body3Medium, Body5Regular, Title1_Bold } from 'styles/Global/typography';
import { TextColor } from 'styles/Global/colorSemantic';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import { GetStatisticsByDateApiData } from 'models/CL/CLApiTypes';
import { ICLUserInfo } from 'models/CL/CLBasicInterface';

interface CLAdminPatientInfoBodyProps {
    userInfo: ICLUserInfo;
    statisticsData: GetStatisticsByDateApiData | undefined;
}

export const CLAdminPatientInfoBody = ({ userInfo, statisticsData }: CLAdminPatientInfoBodyProps) => {
    let ageString = '';
    if(userInfo.birthday) {
        ageString = calculateBirthdayAge(userInfo.birthday || '').ageString;
    }
    return (
        <>
            <InfoHeadContainer>
                {/* <SquareLineSBadge>A-32</SquareLineSBadge> */}
                <NameAndGenderContainer>
                    <span>{userInfo?.name}</span>
                    {userInfo.gender === 'male' ? <GenderMan $width='24px' $height='24px' /> : <GenderWoman $height='24px' $width='24px' />}
                </NameAndGenderContainer>
                <BirthDateAndAgeWrapper>
                    <BirthDate>{ageString ? calculateBirthdayAge(userInfo.birthday || '').formattedBirthday : '미입력'}</BirthDate>
                    <Age>{ageString ? `(${ageString})` : ''}</Age>
                </BirthDateAndAgeWrapper>
            </InfoHeadContainer>
            <InfoBodyLayout>
                <InfoBodyContainer>
                    <CellsContainer>
                        <CellsWrapper>
                            <TitleCell>체중</TitleCell>
                            <BodyCell>{statisticsData?.weight}kg</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>연락처</TitleCell>
                            <BodyCell>{changeMobileForm(statisticsData?.mobile || '')}</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>앱 가입일</TitleCell>
                            <PatientIdCell>{moment(statisticsData?.joinDate).format('YYYY/MM/DD')}</PatientIdCell>
                            {/* <BodyCell>{diagnosisInfo.patientCode || '-'}</BodyCell> */}
                        </CellsWrapper>
                    </CellsContainer>
                    <CellsContainer>
                        <CellsWrapper>
                            <TitleCell>처방 섭취량</TitleCell>
                            <BodyCell>{statisticsData?.targetCalories}kcal</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>수분섭취 목표</TitleCell>
                            <BodyCell>{statisticsData?.targetHydrate}ml</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>칼로리랩 ID</TitleCell>
                            <BodyCell>{statisticsData?.email}</BodyCell>
                        </CellsWrapper>
                    </CellsContainer>
                </InfoBodyContainer>
            </InfoBodyLayout>
        </>
    );
};

const InfoHeadContainer = styled.div`
    display: flex;
    min-width: 240px;
    flex-direction: column;
`;

const NameAndGenderContainer = styled(Title1_Bold)`
    display: flex;
    align-items: center;
    height: 34px;
    margin-top: 6px;
    margin-bottom: 2px;
    gap: 4px;
`;

const BirthDate = styled(Body2Medium)`
    color: ${TextColor.color_text_neutral};
`;

const BirthDateAndAgeWrapper = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 14px;
`;

const Age = styled(Body5Regular)`
    display: flex;
    align-items: center;
    color: ${TextColor.color_text_light};
`;

const InfoBodyLayout = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 568px;
    max-width: 780px;
    // height: 100%;
    padding: 8px 0 14px 0;
`;
const InfoBodyContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100px;
`;

const CellsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 50%;
    height: 100%;
    gap: 14px;
`;

const CellsWrapper = styled.div<CellProps>`
    display: flex;
    width: 100%;
    gap: 4px;
    margin-top: ${(props) => props.$marginTop || '0px'};
`;

const TitleCell = styled.span`
    width: 120px;
    height: 100%;
    line-height: 24px;
    font-size: ${font.size[16]};
    font-weight: ${font.weight.regular};
    color: ${palette.gray};
`;

const BodyCell = styled(Body3Medium)<CellProps>`
    display: flex;
    align-items: center;
    gap: 4px;
    width: ${(props) => props.$width || '256px'};
    // max-width: ${(props) => props.$width};
    color: ${(props) => (props.$noShowDisabled ? TextColor.color_text_light : TextColor.color_text_neutral)};

    ${(props) =>
        props.$noShowDisabled &&
        `& .textDecoration {
            text-decoration: line-through;
            -webkit-text-decoration: line-through;
            text-decoration-line: line-through;
            text-decoration-style: solid;
        }`}

    white-space: pre-wrap; /* CSS3 */
    white-space: -moz-pre-wrap; /* Firefox */
    white-space: -pre-wrap; /* Opera <7 */
    white-space: -o-pre-wrap; /* Opera 7 */
    word-wrap: break-word; /* Internet Explorer */
    overflow-wrap: break-word; /* CSS3 */
`;

const PatientIdCell = styled(Body3Medium)<CellProps>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: ${(props) => props.$width || '220px'};
    color: ${TextColor.color_text_neutral};
    white-space: pre-wrap; /* CSS3 */
    white-space: -moz-pre-wrap; /* Firefox */
    white-space: -pre-wrap; /* Opera <7 */
    white-space: -o-pre-wrap; /* Opera 7 */
    word-wrap: break-word; /* Internet Explorer */
    overflow-wrap: break-word; /* CSS3 */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DoctorText = styled.span`
    font-size: ${font.size[15]};
    font-weight: ${font.weight.regular};
    color: ${palette.gray};
`;
